import { Box, Container, IconButton, Paper, Typography } from "@mui/material";
import { Close as CloseIcon, Create as CreateIcon, Print as PrintIcon } from "@mui/icons-material";
import React, { useEffect, useRef, useState } from "react";
import ReactToPrint from "react-to-print";
import AHMIS, { API_URL } from "../../../api/api";
import AddAlertMessage from "../../../components/alert/Alert";
import Spinner from "../../../components/loader/Loader";
import { AppUtils } from "../../../utils/appUtils";
import { ERROR, SOMETHING_WENT_WRONG, SUCCESS } from "../../../utils/constants";
import HealthServiceCardHeader from "../../users/components/health-service-card-header/HealthServiceCardHeader";
import LabReportSubHeader from "../../../components/lab-report-sub-header/LabReportSubHeader";
import HematologicalTestForm from "./test-forms/HematologicalTestForm";
import HematologicalTestReport from "./test-reports/HematologicalTestReport";
import BioChemistryTestForm from "./test-forms/BioChemistryTestForm";
import BioChemistryTestReport from "./test-reports/BioChemistryTestReport";
import LiverFunctionTestForm from "./test-forms/LiverFunctionTestForm";
import LiverFunctionTestReport from "./test-reports/LiverFunctionTestReport";
import LipidProfileTestForm from "./test-forms/LipidProfileTestForm";
import LipidProfileTestReport from "./test-reports/LipidProfileTestReport";
import UrineTestForm from "./test-forms/UrineTestForm";
import UrineTestReport from "./test-reports/UrineTestReport";
import ImmunologyTestForm from "./test-forms/ImmunologyTestForm";
import ImmunologyTestReport from "./test-reports/ImmunologyTestReport";
import MicroscopicTestForm from "./test-forms/MicroscopicTestForm";
import MicroscopicTestReport from "./test-reports/MicroscopicTestReport";
import StoolTestForm from "./test-forms/StoolTestForm";
import StoolTestReport from "./test-reports/StoolTestReport";
import styles from "./style";
import { AppMisc } from "../../../misc/appMisc";

export default function LabTestDetails() {
  const classes = styles();
  const [labInfo, setLabInfo] = useState(null);
  const [labService, setLabService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editSections, setEditSections] = useState({});
  const componentRef = useRef();

  var labInfoId = AppUtils.getUrlParam("id");

  const getLabInfo = () => {
    AHMIS.get(API_URL.labInfo + "/" + labInfoId)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          setLabInfo(jsondata.data);
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      })
      .catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  };

  const getLabService = () => {
    AHMIS.get(`${API_URL.labService}/labInfoId/${labInfoId}`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS && jsondata.data && jsondata.data.length > 0) {
          setLabService(jsondata.data[0]);
        }
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (labInfoId) {
      getLabInfo();
    }
  }, [labInfoId]);

  useEffect(() => {
    if (labInfo) {
      getLabService();
    }
  }, [labInfo]);

  const toggleEditSection = (section) => {
    setEditSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const cancelEditSection = (section) => {
    setEditSections(prev => ({ ...prev, [section]: false }));
  };

  const handleSaveLabService = (data) => {
    let payload = {
      labInfoId: labInfoId,
      gender: labInfo?.gender,
      dartaaMiti: labInfo?.dartaaMiti,
      ...data,
    };
    if (labService?.id) {
      payload.id = labService.id;
    }
    AHMIS.post(API_URL.labService, payload)
      .then(response => {
        if (response.data.type === SUCCESS) {
          getLabService();
          setEditSections({});
        }
        AddAlertMessage({ type: response.data.type, message: response.data.message });
      })
      .catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  };

  const allTestSections = [
    { key: "heamatologyTest", reportTemplate: "HEMATOLOGICAL_TEST", title: "HEMATOLOGICAL TEST RESULT", Form: HematologicalTestForm, Report: HematologicalTestReport },
    { key: "bioChemistryTest", reportTemplate: "BIOCHEMICAL_TEST", title: "BIOCHEMISTRY TEST RESULT", Form: BioChemistryTestForm, Report: BioChemistryTestReport },
    { key: "liverFuctionTest", reportTemplate: "LIVER_FUNCTION_TEST", title: "LIVER FUNCTION TEST RESULT", Form: LiverFunctionTestForm, Report: LiverFunctionTestReport },
    { key: "lipidProfileTest", reportTemplate: "LIPID_PROFILE_TEST", title: "LIPID PROFILE TEST RESULT", Form: LipidProfileTestForm, Report: LipidProfileTestReport },
    { key: "urineTest", reportTemplate: "URINE_TEST", title: "URINE TEST RESULT", Form: UrineTestForm, Report: UrineTestReport },
    { key: "immunologyTest", reportTemplate: "IMMUNOLOGY_TEST", title: "IMMUNOLOGY TEST RESULT", Form: ImmunologyTestForm, Report: ImmunologyTestReport },
    { key: "microscopicTest", reportTemplate: "MICROSCOPIC_TEST", title: "MICROSCOPIC TEST RESULT", Form: MicroscopicTestForm, Report: MicroscopicTestReport },
    { key: "stoolTest", reportTemplate: "STOOL_TEST", title: "STOOL TEST RESULT", Form: StoolTestForm, Report: StoolTestReport },
  ];

  const labTemplates = (labInfo?.labSewa || []).map(sewa => sewa.reportTemplate);
  const testSections = allTestSections.filter(section => labTemplates.includes(section.reportTemplate));

  return (
    <Box>
      <Box display="flex" className="border-bottom-heading" flexDirection="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">प्रयोगशाला रिपोर्ट</Typography>
        <ReactToPrint
          trigger={() => (
            <IconButton className="print-none" aria-label="print">
              <PrintIcon />
            </IconButton>
          )}
          content={() => componentRef.current}
        />
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Spinner />
        </Box>
      ) : (
        <Box className={classes.labTestDetailsContainer} ref={componentRef}>
          <Box className={classes.printOnlyHeader}>
            <HealthServiceCardHeader />
            {labInfo && (
              <Box className={classes.labReportSubHeader} mb={2}>
                <LabReportSubHeader data={labInfo} />
              </Box>
            )}
          </Box>
          {testSections.map(({ key, title, Form, Report, reportTemplate }) => (
            <Paper key={key} className={classes.paper}>
              <Box className={classes.paperHeader}>
                <Typography variant="h6">{title}</Typography>
                <Box className={classes.paperActionButtons}>
                  {editSections[key] ? (
                    <IconButton aria-label="cancel" onClick={() => cancelEditSection(key)}>
                      <CloseIcon />
                    </IconButton>
                  ) : (
                    <IconButton className="print-none" aria-label="edit" onClick={() => toggleEditSection(key)}>
                      <CreateIcon />
                    </IconButton>
                  )}
                </Box>
              </Box>
              {editSections[key] ? (
                <Form
                  defaultValues={labService?.[key] || {}}
                  onSubmit={(data) => {
                    let payload = {};
                    testSections.forEach(({ key: sectionKey }) => {
                      if (labService?.[sectionKey]) {
                        payload[sectionKey] = labService[sectionKey];
                      }
                    });
                    payload[key] = data;
                    handleSaveLabService(payload);
                  }}
                />
              ) : (
                <Report defaultValues={labService?.[key] || {}} gender={labInfo?.gender} reportTemplate={reportTemplate} />
              )}
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
